import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import {
    addOfferToCard,
    fetchCard,
    fetchCardOffers, getCardCodes,
    removeCard,
    removeOfferFromCard,
    updateCard,
} from "../api/cardapi";
import DataTable from "../components/DataTable";
import DataCard from "../components/DataCard";
import CustomModal from "../components/CustomModal";
import { fetchOffers } from "../api/offerapi";
import Tabs from "../components/Tabs";
import {addCode, deleteCode} from "../api/codeApi";
import {getQrImageForCode} from "../func";

export default function Card() {
    const { id } = useParams();
    const { token } = useAuth();
    const [card, setCard] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchCardData = async () => {
        setLoading(true);
        try {
            const response = await fetchCard(id);
            if (response?.success) {
                setCard(response.data);
                setError(null);
            } else {
                setError(response?.message || "Failed to fetch card");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setCard((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSave = () => {
        updateCard(card).then(console.log);
    };

    const handleDelete = async() => {
        await removeCard(id)
        if (window.history.length > 1) navigate(-1);
        else navigate("/cards");
    };

    useEffect(() => {
        fetchCardData();
    }, [id, token]);

    if (loading) return <p className="m-3">Loading card...</p>;
    if (error) return <p className="m-3 text-danger">❌ {error}</p>;
    if (!card) return <p className="m-3">No card found.</p>;

    return (
        <div className="container mt-4">
            <DataCard
                title={"Card details"}
                edit
                onRemove={handleDelete}
                onChange={handleChange}
                onSave={handleSave}
                items={[
                    { label: "Name", value: card.Name, editable: true },
                    { label: "Description", value: card.Description, editable: true },
                    {
                        label: "Expires",
                        key: "expires_at",
                        value: card.expires_at
                            ? new Date(card.expires_at).toLocaleDateString()
                            : "Never",
                        editable: true,
                        format: { type: "date" },
                    },
                    {
                        label: "Public",
                        key: "isPublic",
                        value: card.isPublic,
                        editable: true,
                        format: { type: "bool" },
                    },
                    {
                        label: "Sharable",
                        key: "isSharable",
                        value: card.isSharable,
                        editable: true,
                        format: { type: "bool" },
                    },
                    {
                        label: "Created",
                        value: new Date(card.created_at).toLocaleString(),
                    },
                    {
                        label: "Updated",
                        value: new Date(card.updated_at).toLocaleString(),
                    },
                ]}
            />

            {/* ✅ Tabs section */}
            <Tabs
                tabs={[
                    {
                        key: "offers",
                        label: "Offers",
                        component: <CardOffersTable cardId={id} />,
                    },
                    {
                        key: "codes",
                        label: "Codes",
                        component: <CardCodesTable cardId={id} />,
                    },
                ]}
            />
        </div>
    );
}

/* -------------------------------------------
   ✅ CardOffersTable (fully self-contained)
--------------------------------------------*/
function CardOffersTable({ cardId }) {
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [addOffer, setAddOffer] = useState(false);

    const loadOffers = async () => {
        const response = await fetchCardOffers(cardId);
        if (response?.success) setOffers(response.data);
    };

    const handleAddOffer = async (offer) => {
        await addOfferToCard(cardId, offer.OfferID);
        setAddOffer(false);
        loadOffers();
    };

    const handleRemoveOffer = async (offer) => {
        await removeOfferFromCard(cardId, offer.OfferID);
        loadOffers();
    };

    useEffect(() => {
        loadOffers();
    }, [cardId]);

    return (
        <>
            <DataTable
                title="Offers"
                columns={[
                    { key: "Text", label: "Title", searchable: true },
                    { key: "LocationName", label: "Location", filterable: true },
                    { key: "expires_at", label: "Expires", filterable: true },
                ]}
                data={(offers || []).map((o) => ({
                    ...o,
                    LocationName: o.Location?.Name || "—",
                    expires_at: o.expires_at
                        ? new Date(o.expires_at).toLocaleDateString()
                        : "—",
                }))}
                add
                remove
                onAdd={() => setAddOffer(true)}
                onRemove={handleRemoveOffer}
                onRowClick={(offer) => navigate("/offers/" + offer.OfferID)}
            />

            {/* ✅ modal is fully contained here */}
            {addOffer && (
                <AddOfferModal
                    show={addOffer}
                    onClose={() => setAddOffer(false)}
                    onAdd={handleAddOffer}
                />
            )}

        </>
    );
}

/* -------------------------------------------
   ✅ AddOfferModal (private to CardOffersTable)
--------------------------------------------*/
function AddOfferModal({ show, onClose, onAdd }) {
    const [offers, setOffers] = useState([]);

    useEffect(() => {
        if (show) {
            fetchOffers().then((r) => setOffers(r.data ?? []));
        }
    }, [show]);

    const handleRowClick = (row) => {
        onAdd(row);
        onClose();
    };

    return (
        <CustomModal
            title="Add Offer to Card"
            show={show}
            id="addOfferModal"
            onClose={onClose}
        >
            <DataTable
                title="Available Offers"
                columns={[
                    { key: "Text", label: "Offer", searchable: true },
                    { key: "LocationName", label: "Location", filterable: true },
                    { key: "expires_at", label: "Expires", filterable: true },
                ]}
                data={(offers || []).map((o) => ({
                    ...o,
                    LocationName: o.Location?.Name || "—",
                    expires_at: o.expires_at
                        ? new Date(o.expires_at).toLocaleDateString()
                        : "—",
                }))}
                onRowClick={handleRowClick}
            />
        </CustomModal>
    );
}

/* -------------------------------------------
   ✅ Codes tab placeholder
--------------------------------------------*/
function CardCodesTable({ cardId }) {
    const [codes, setCodes] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [qrCode, setQrCode] = useState(null)

    async function fetchData() {
        const res = await getCardCodes(cardId);
        setCodes(res.data ?? []);
    }

    useEffect(() => {
        fetchData();
    }, [cardId]);

    async function handleAdd(newCodeData) {
        await addCode(cardId, newCodeData);
        fetchData();
    }

    async function handleRemove(row) {
        // Optimistically update UI
        const prevCodes = [...codes];
        setCodes((prev) => prev.filter((c) => c.CardCodeID !== row.CardCodeID));

        try {
            const res = await deleteCode(row.CardCodeID);
            if (!res?.success) throw new Error(res?.message || "Failed to remove code");
        } catch (err) {
            setCodes(prevCodes);
        }
    }

    function handleExport(){
        console.log(codes)
    }


    return (
        <>
            <DataTable
                title="Codes"
                rowAction={{
                    title: "QR",
                    onClick: (row) =>setQrCode(row.Code)
                }}
                add
                remove
                onAdd={() => setShowAdd(true)}
                onRemove={handleRemove}
                columns={[
                    { key: "Code", label: "Code", searchable: true },
                    { key: "isCountable", label: "Countable", filterable: true },
                    { key: "Remaining", label: "Remaining" },
                    { key: "expires_at", label: "Expires" },
                ]}
                data={(codes || []).map((c) => ({
                    ...c,
                    isCountable: c.isCountable ? "Yes": "No",
                    expires_at: c.expires_at
                        ? new Date(c.expires_at).toLocaleDateString()
                        : "—",
                }))}
            />

            {showAdd && (
                <AddCodeModal
                    show={showAdd}
                    onClose={() => setShowAdd(false)}
                    onAdd={handleAdd}
                />
            )}
            {qrCode && <QrCodeModal code={qrCode} onClose={()=>{setQrCode(null)}}/>}
        </>
    );
}

function AddCodeModal({ show, onClose, onAdd }) {
    const [code, setCode] = useState("");
    const [isCountable, setIsCountable] = useState(false);
    const [remaining, setRemaining] = useState("");
    const [expiresAt, setExpiresAt] = useState("");

    function handleSave() {
        const payload = {
            Code: code || null,
            isCountable,
            Remaining: isCountable ? Number(remaining) || 0 : null,
            expires_at: expiresAt || null,
        };
        onAdd(payload);
        onClose();
    }

    return (
        <CustomModal title="New Code" show={show} onClose={onClose} hideFooter>
            <div className="p-3">
                {/* Code */}
                <div className="mb-3">
                    <label className="form-label">Code (optional)</label>
                    <input
                        className="form-control"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Leave blank to auto-generate"
                    />
                </div>

                {/* isCountable */}
                <div className="form-check mb-3">
                    <input
                        type="checkbox"
                        id="countable"
                        className="form-check-input"
                        checked={isCountable}
                        onChange={(e) => setIsCountable(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="countable">
                        Countable (decrease Remaining on use)
                    </label>
                </div>

                {/* Remaining */}
                {isCountable && (
                    <div className="mb-3">
                        <label className="form-label">Uses</label>
                        <input
                            type="number"
                            className="form-control"
                            value={remaining}
                            onChange={(e) => setRemaining(e.target.value)}
                            min="0"
                            placeholder="e.g. 10"
                        />
                    </div>
                )}

                {/* Expires */}
                <div className="mb-3">
                    <label className="form-label">Expires (optional)</label>
                    <input
                        type="date"
                        className="form-control"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                    />
                </div>

                {/* Buttons */}
                <div className="d-flex justify-content-end">
                    <button className="btn btn-success" onClick={handleSave}>
                        Save
                    </button>
                </div>
            </div>
        </CustomModal>
    );
}

function QrCodeModal({ code, onClose }) {
    const [imgSrc, setImgSrc] = useState(null);

    useEffect(() => {
        if (code) {
            getQrImageForCode(code).then((src) => setImgSrc(src));
        }
    }, [code]);

    async function handleDownload() {
        try {
            const response = await fetch(imgSrc);
            const blob = await response.blob();

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `QR_${code}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // optional cleanup
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Failed to download QR:", err);
        }
    }


    return (
        <CustomModal title="QR Code" show={!!code} onClose={onClose}>
            <div className="text-center p-3">
                <img
                    src={imgSrc}
                    alt={`QR for ${code}`}
                    style={{ maxWidth: "100%", border: "1px solid #ddd" }}
                />

                <div className="mt-3 d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={handleDownload}>
                        Download
                    </button>
                </div>
            </div>
        </CustomModal>
    );
}

